import React, { useRef, useEffect, useState } from 'react';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import ChatHeader from './ChatHeader';
import TypingIndicator from './TypingIndicator';
import { sendChatMessage, checkHealth } from '../utils/apiService';

const ChatBot = () => {
  const [messages, setMessages] = useState([
    { 
      id: 'welcome',
      text: "Hello! I'm your AI banking assistant powered by AWS Bedrock. How can I help with your banking needs today?",
      sender: 'bot',
      timestamp: new Date().toISOString()
    }
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isServiceHealthy, setIsServiceHealthy] = useState(true);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);

  // Generate a unique conversation ID when the component mounts
  useEffect(() => {
    setConversationId(`conv-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`);
  }, []);

  // Check API health on component mount
  useEffect(() => {
    const checkApiHealth = async () => {
      try {
        await checkHealth();
        setIsServiceHealthy(true);
      } catch (error) {
        console.error('API health check failed:', error);
        setIsServiceHealthy(false);
        setError('Our service is currently unavailable. Please try again later.');
      }
    };
    
    checkApiHealth();
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    if (!isServiceHealthy) {
      setError('Our service is currently unavailable. Please try again later.');
      return;
    }

    // Reset any previous errors
    setError(null);
    
    // Add user message to the chat
    const userMessage = { 
      id: `user-${Date.now()}`,
      text, 
      sender: 'user',
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setLoading(true);

    try {
      // For first message, don't send conversationId
      // For subsequent messages, include the conversationId
      const isFirstMessage = messages.length === 1; // Only welcome message exists
      const response = await sendChatMessage(
        text, 
        isFirstMessage ? null : conversationId
      );
      
      // Add bot response to the chat
      const botMessage = { 
        id: `bot-${Date.now()}`,
        text: response.reply, 
        sender: 'bot',
        sources: response.sources || [],
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, botMessage]);
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Sorry, there was a problem connecting to the AI service. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow-2xl">
      <ChatHeader />
      
      <div className="flex-grow overflow-y-auto p-4" style={{ maxHeight: 'calc(100vh - 200px)' }}>
        {messages.map(message => (
          <ChatMessage key={message.id} message={message} />
        ))}
        
        {loading && <TypingIndicator />}
        
        {error && (
          <div className="p-3 bg-red-100 text-red-800 rounded-lg mb-4">
            {error}
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      <div className="p-4 bg-gray-50 rounded-b-lg">
        <ChatInput onSendMessage={sendMessage} isLoading={loading} />
      </div>
    </div>
  );
};

export default ChatBot;